import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { BadgeCheck, MessageCircle, ShieldCheck, Smartphone, X } from "lucide-react";
import { appApi } from "@/api";
import { session } from "@/lib/session";

interface BindMobileModalProps {
  open: boolean;
  title?: string;
  description?: string;
  onBound: (mobile: string) => void;
  onCancel: () => void;
}

const SMS_COUNTDOWN_SECONDS = 60;

export default function BindMobileModal({
  open,
  title = "绑定手机号",
  description = "绑定手机号后，我们可以为您提供更完整的预约服务，并及时同步订单进度和服务通知。",
  onBound,
  onCancel,
}: BindMobileModalProps) {
  const [mobile, setMobile] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [notice, setNotice] = useState("");
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (!open || countdown <= 0) {
      return undefined;
    }
    const timer = window.setTimeout(() => setCountdown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown, open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const root = document.getElementById("root");
    const previousOverflow = root?.style.overflow;
    if (root) {
      root.style.overflow = "hidden";
    }
    return () => {
      if (root) {
        root.style.overflow = previousOverflow || "";
      }
    };
  }, [open]);

  if (!open) {
    return null;
  }

  const sendCaptcha = async () => {
    const nextMobile = mobile.trim();
    if (!/^1\d{10}$/.test(nextMobile)) {
      setNotice("请输入正确的手机号");
      return;
    }

    setSending(true);
    setNotice("");
    try {
      await appApi.sendSmsCode({ mobile: nextMobile, type: 1 });
      setCountdown(SMS_COUNTDOWN_SECONDS);
      setNotice("验证码已发送，请查收短信");
    } catch (requestError) {
      setNotice(requestError instanceof Error ? requestError.message : "验证码发送失败");
    } finally {
      setSending(false);
    }
  };

  const submitBind = async () => {
    const nextMobile = mobile.trim();
    const nextCaptcha = captcha.trim();
    if (!/^1\d{10}$/.test(nextMobile)) {
      setNotice("请输入正确的手机号");
      return;
    }
    if (!/^\d{6}$/.test(nextCaptcha)) {
      setNotice("请输入 6 位短信验证码");
      return;
    }

    setSubmitting(true);
    setNotice("");
    try {
      const loginPayload = await appApi.bindMobile({ mobile: nextMobile, captcha: nextCaptcha });
      session.saveLogin(loginPayload);
      setCaptcha("");
      setNotice("");
      onBound(loginPayload.mobile || nextMobile);
    } catch (requestError) {
      setNotice(requestError instanceof Error ? requestError.message : "手机号绑定失败");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="customer-fixed-shell fixed inset-0 z-[120] flex items-end justify-center px-3 pb-3 sm:items-center sm:p-5"
      style={{ background: "rgba(16, 18, 24, 0.58)", backdropFilter: "blur(10px)" }}
      aria-modal="true"
      role="dialog"
    >
      <div className="w-full overflow-hidden bg-white shadow-2xl" style={{ maxWidth: 440, borderRadius: 28 }}>
        <div className="relative px-5 pb-5 pt-5">
          <div
            className="absolute left-0 right-0 top-0 h-1"
            style={{ background: "linear-gradient(90deg, #FF8A3D 0%, #FF5C00 52%, #16A085 100%)" }}
          />
          <button
            type="button"
            onClick={onCancel}
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full"
            style={{ background: "#F6F6F6", color: "#8A8A8A", border: "none" }}
            aria-label="关闭绑定手机号弹窗"
            title="关闭"
          >
            <X size={20} strokeWidth={2.4} />
          </button>
          <div className="flex items-start gap-3">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl"
              style={{ background: "linear-gradient(135deg, #FFF1E8 0%, #FFE0CC 100%)", color: "#FF5C00" }}
            >
              <Smartphone size={25} strokeWidth={2.4} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: "#FF6A18" }}>
                完善服务信息
              </p>
              <h3 className="mt-1 text-[22px] font-black leading-tight" style={{ color: "#171717", letterSpacing: 0 }}>
                {title}
              </h3>
              <p className="mt-2 text-sm leading-6" style={{ color: "#6F6F6F" }}>
                {description}
              </p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2 rounded-2xl px-3 py-2" style={{ background: "#F7F4F0", color: "#555" }}>
              <BadgeCheck size={16} />
              <span className="text-xs font-semibold">服务更完整</span>
            </div>
            <div className="flex items-center gap-2 rounded-2xl px-3 py-2" style={{ background: "#EEF8F5", color: "#217D68" }}>
              <MessageCircle size={16} />
              <span className="text-xs font-semibold">通知不错过</span>
            </div>
          </div>

          <div className="mt-5 space-y-3">
            <label className="block">
              <span className="mb-2 block text-xs font-semibold" style={{ color: "#555" }}>
                手机号
              </span>
              <div className="flex items-center gap-3 rounded-2xl px-4" style={{ height: 56, background: "#F8F8F8", border: "1px solid #ECECEC" }}>
                <Smartphone size={20} color="#9A9A9A" />
                <input
                  value={mobile}
                  onChange={(event) => setMobile(event.target.value.replace(/\D/g, "").slice(0, 11))}
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="请输入手机号"
                  className="min-w-0 flex-1 bg-transparent text-base font-semibold outline-none"
                  style={{ color: "#1F1F1F" }}
                />
              </div>
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-semibold" style={{ color: "#555" }}>
                短信验证码
              </span>
              <div className="flex items-center gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-3 rounded-2xl px-4" style={{ height: 56, background: "#F8F8F8", border: "1px solid #ECECEC" }}>
                  <ShieldCheck size={20} color="#9A9A9A" />
                  <input
                    value={captcha}
                    onChange={(event) => setCaptcha(event.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="请输入验证码"
                    className="min-w-0 flex-1 bg-transparent text-base font-semibold outline-none"
                    style={{ color: "#1F1F1F" }}
                  />
                </div>
                <button
                  type="button"
                  disabled={sending || countdown > 0}
                  onClick={() => void sendCaptcha()}
                  className="shrink-0 rounded-2xl px-3 text-sm font-bold"
                  style={{
                    height: 56,
                    minWidth: 104,
                    background: sending || countdown > 0 ? "#F0F0F0" : "#FFF0E6",
                    color: sending || countdown > 0 ? "#999" : "#F26A1B",
                    border: "none",
                  }}
                >
                  {sending ? "发送中" : countdown > 0 ? `${countdown}s` : "获取验证码"}
                </button>
              </div>
            </label>
          </div>

          {notice ? (
            <div className="mt-4 rounded-2xl px-3 py-2 text-sm font-semibold" style={{ background: "#FFF7F2", color: "#D95C12" }}>
              {notice}
            </div>
          ) : null}

          <button
            type="button"
            disabled={submitting}
            onClick={() => void submitBind()}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full py-4 text-base font-black text-white shadow-lg"
            style={{
              background: "linear-gradient(135deg, #FF8A3D 0%, #FF5C00 100%)",
              border: "none",
              boxShadow: "0 12px 28px rgba(255, 92, 0, 0.26)",
              opacity: submitting ? 0.72 : 1,
            }}
          >
            <ShieldCheck size={20} />
            <span>{submitting ? "绑定中..." : "确认绑定并继续"}</span>
          </button>

          <p className="mt-3 text-center text-xs leading-5" style={{ color: "#999" }}>
            暂不绑定将返回首页浏览技师列表，预约和订单服务需要完成绑定
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
